/**
 * Import-Reiter der Einstellungen: die Excel „Kundenübersicht" einspielen.
 *
 * Bis hierher gab es den Import nur als zwei Endpunkte, die jemand per `curl`
 * aufrief (so stand er auch in der ANLEITUNG) — und damit fand die Ablösung
 * der Excel praktisch nicht statt. Der Reiter hängt bewusst dort, wo die
 * übrigen Wartungs-Stammdaten gepflegt werden (Softwarekatalog, Merkmale):
 * `/settings` setzt bereits Admin/BL/Stellv. voraus und deckt sich damit mit
 * dem Rollen-Gate der beiden Endpunkte (`IMPORT_ROLES`).
 *
 * Ablauf ist erzwungen, nicht empfohlen: Ohne Vorschau zur AKTUELL gewählten
 * Datei bleibt „Übernehmen" gesperrt. Wechselt die Datei, verfällt die
 * Vorschau — sonst könnte jemand die Zahlen der einen Datei lesen und die
 * andere einspielen.
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  FileInput,
  Group,
  List,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ImportPreview, ImportResult } from "@msc/shared";

import { useImportCommit, useImportPreview } from "../../hooks/useMaintenance.js";
import { formatIsoDate } from "../../lib/date.js";

/** Zahlenkachel der Vorschau — dieselbe Form für „würde anlegen" und „hat angelegt". */
function Counter({ label, value }: { label: string; value: number }) {
  return (
    <Card withBorder padding="sm" style={{ minWidth: 150 }}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text fw={700} size="xl">
        {value}
      </Text>
    </Card>
  );
}

/**
 * Eine Auffälligkeit der Vorschau. Leere Listen werden NICHT ausgeblendet,
 * sondern mit ihrem Ruhezustand gezeigt („keine") — sonst ist nicht zu
 * unterscheiden, ob geprüft wurde oder ob es die Prüfung gar nicht gibt.
 */
function Finding({
  title,
  hint,
  count,
  emptyLabel,
  children,
}: {
  title: string;
  hint: string;
  count: number;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Paper p="md" withBorder>
      <Group gap="xs" mb={4}>
        <Text fw={600} size="sm">
          {title}
        </Text>
        <Badge size="sm" variant="light" color={count > 0 ? "orange" : "gray"}>
          {count}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed" mb="sm">
        {hint}
      </Text>
      {count === 0 ? (
        <Text size="sm" c="dimmed">
          {emptyLabel}
        </Text>
      ) : (
        children
      )}
    </Paper>
  );
}

export function MaintenanceImportTab() {
  const { t } = useTranslation();
  const preview = useImportPreview();
  const commit = useImportCommit();

  const [file, setFile] = useState<File | null>(null);
  // Stichtag der DATEI, nicht des Imports: leer = heute. Er entscheidet, ob
  // ein bereits erfasster Stand als aktueller gilt als die Datei.
  const [asOf, setAsOf] = useState("");
  // Die Vorschau gehört zu GENAU dieser Datei. `previewedFile` ist der
  // Identitätsbeweis: nur wenn er auf dieselbe File-Instanz zeigt wie `file`,
  // darf übernommen werden.
  const [previewedFile, setPreviewedFile] = useState<File | null>(null);
  const [data, setData] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function invalidatePreview() {
    setPreviewedFile(null);
    setData(null);
    setResult(null);
    setError(null);
  }

  function handleFileChange(next: File | null) {
    setFile(next);
    setPreviewedFile(null);
    setData(null);
    setResult(null);
    setError(null);
  }

  async function handlePreview() {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const response = await preview.mutateAsync({ file, asOf: asOf || undefined });
      setData(response);
      setPreviewedFile(file);
    } catch (err) {
      setData(null);
      setPreviewedFile(null);
      setError(err instanceof Error ? err.message : t("pages.settings.import.previewError"));
    }
  }

  async function handleCommit() {
    if (!file) return;
    setError(null);
    try {
      setResult(await commit.mutateAsync({ file, asOf: asOf || undefined }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.settings.import.commitError"));
    }
  }

  const previewValid = data !== null && previewedFile === file;

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t("pages.settings.import.subtitle")}
      </Text>

      <Paper p="md" withBorder>
        <Group align="flex-end" gap="sm" wrap="wrap">
          <FileInput
            label={t("pages.settings.import.fileLabel")}
            description={t("pages.settings.import.fileHint")}
            placeholder={t("pages.settings.import.filePlaceholder")}
            accept=".xlsx"
            value={file}
            onChange={handleFileChange}
            clearable
            style={{ minWidth: 320 }}
          />
          <TextInput
            label={t("pages.settings.import.asOfLabel")}
            description={t("pages.settings.import.asOfHint")}
            type="date"
            value={asOf}
            onChange={(e) => {
              setAsOf(e.currentTarget.value);
              // Der Stichtag ändert das Ergebnis — die alte Vorschau gehört
              // nicht mehr zu dem, was übernommen würde.
              invalidatePreview();
            }}
            style={{ minWidth: 200 }}
          />
          <Button
            disabled={!file}
            loading={preview.isPending}
            onClick={() => void handlePreview()}
          >
            {t("pages.settings.import.previewButton")}
          </Button>
          <Button
            color="orange"
            disabled={!previewValid}
            loading={commit.isPending}
            onClick={() => void handleCommit()}
          >
            {t("pages.settings.import.commitButton")}
          </Button>
        </Group>
        {!previewValid && file && !preview.isPending && (
          <Text size="xs" c="dimmed" mt="xs">
            {t("pages.settings.import.previewFirstHint")}
          </Text>
        )}
      </Paper>

      {error && (
        <Alert color="red" title={t("common.error")}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert color="green" title={t("pages.settings.import.commitDoneTitle")}>
          {t("pages.settings.import.commitDoneText", {
            systems: result.systems,
            states: result.states,
            lifecycles: result.lifecycles,
          })}
        </Alert>
      )}

      {previewValid && data && (
        <Stack gap="md">
          <Title order={4}>{t("pages.settings.import.previewTitle")}</Title>
          <Text size="sm" c="dimmed">
            {t("pages.settings.import.previewAsOf", { date: formatIsoDate(data.asOf) })}
          </Text>
          <Group gap="sm" wrap="wrap">
            <Counter label={t("pages.settings.import.counterSystems")} value={data.systems} />
            <Counter label={t("pages.settings.import.counterStates")} value={data.states} />
            <Counter
              label={t("pages.settings.import.counterLifecycles")}
              value={data.lifecycles}
            />
            <Counter
              label={t("pages.settings.import.counterSkippedStates")}
              value={data.skippedStates}
            />
          </Group>

          {/* Was die Datei an ERFASSTEN Ständen verändert — der Fall, in dem
              ein zweiter Lauf gepflegte Daten zurückdreht. */}
          <Finding
            title={t("pages.settings.import.overwrittenTitle")}
            hint={t("pages.settings.import.overwrittenHint")}
            count={data.overwrittenStates.length}
            emptyLabel={t("pages.settings.import.overwrittenEmpty")}
          >
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("common.customer")}</Table.Th>
                  <Table.Th>{t("pages.wartung.tableProduct")}</Table.Th>
                  <Table.Th>{t("pages.settings.import.tableExistingState")}</Table.Th>
                  <Table.Th>{t("pages.settings.import.tableFileState")}</Table.Th>
                  <Table.Th>{t("pages.settings.import.tableEffect")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.overwrittenStates.map((entry, index) => (
                  <Table.Tr key={`${entry.customerName}|${entry.productName}|${index}`}>
                    <Table.Td>{entry.customerName}</Table.Td>
                    <Table.Td>{entry.productName}</Table.Td>
                    <Table.Td>
                      {entry.existingVersionLabel}
                      <Text size="xs" c="dimmed">
                        {t("pages.settings.import.stateAsOf", {
                          date: formatIsoDate(entry.existingAsOf),
                          name: entry.existingConfirmedBy ?? t("common.dashed"),
                        })}
                      </Text>
                    </Table.Td>
                    <Table.Td>{entry.fileVersionLabel}</Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={entry.skipped ? "gray" : "orange"}
                      >
                        {entry.skipped
                          ? t("pages.settings.import.effectSkipped")
                          : t("pages.settings.import.effectOverwritten")}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Finding>

          <Finding
            title={t("pages.settings.import.unmatchedTitle")}
            hint={t("pages.settings.import.unmatchedHint")}
            count={data.unmatchedCustomers.length}
            emptyLabel={t("pages.settings.import.unmatchedEmpty")}
          >
            <List size="sm">
              {data.unmatchedCustomers.map((name, index) => (
                <List.Item key={`${name}-${index}`}>{name}</List.Item>
              ))}
            </List>
          </Finding>

          <Finding
            title={t("pages.settings.import.conflictsTitle")}
            hint={t("pages.settings.import.conflictsHint")}
            count={data.conflicts.length}
            emptyLabel={t("pages.settings.import.conflictsEmpty")}
          >
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("pages.wartung.tableProduct")}</Table.Th>
                  <Table.Th>{t("pages.wartung.tableVersion")}</Table.Th>
                  <Table.Th>{t("pages.settings.import.tableDates")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.conflicts.map((conflict) => (
                  <Table.Tr key={`${conflict.product}|${conflict.versionLabel}`}>
                    <Table.Td>{conflict.product}</Table.Td>
                    <Table.Td>{conflict.versionLabel}</Table.Td>
                    <Table.Td>{conflict.dates.join(", ")}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Finding>

          {/* Was die Datei NICHT durchsetzt: handgepflegte Wartungsenden
              bleiben stehen (Spec §7). Ohne diese Liste sähe der Anwender
              nach dem Lauf einen Wert, den die Datei anders vorgibt, und
              hielte ihn für einen Fehler. */}
          <Finding
            title={t("pages.settings.import.protectedTitle")}
            hint={t("pages.settings.import.protectedHint")}
            count={data.protectedMaintenanceEnds.length}
            emptyLabel={t("pages.settings.import.protectedEmpty")}
          >
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("pages.wartung.tableProduct")}</Table.Th>
                  <Table.Th>{t("pages.wartung.tableVersion")}</Table.Th>
                  <Table.Th>{t("pages.settings.import.tableExistingEnd")}</Table.Th>
                  <Table.Th>{t("pages.settings.import.tableFileEnd")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.protectedMaintenanceEnds.map((entry) => (
                  <Table.Tr key={`${entry.versionLabel}|${entry.fileEnd}`}>
                    <Table.Td>{entry.productName}</Table.Td>
                    <Table.Td>{entry.versionLabel}</Table.Td>
                    <Table.Td>
                      {formatIsoDate(entry.existingEnd)}
                      {entry.existingSource && (
                        <Text size="xs" c="dimmed">
                          {entry.existingSource}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text td="line-through" c="dimmed">
                        {formatIsoDate(entry.fileEnd)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Finding>

          <Finding
            title={t("pages.settings.import.unparsedTitle")}
            hint={t("pages.settings.import.unparsedHint")}
            count={data.unparsedMaintenanceEnds.length}
            emptyLabel={t("pages.settings.import.unparsedEmpty")}
          >
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("common.customer")}</Table.Th>
                  <Table.Th>{t("pages.settings.import.tableRawValue")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.unparsedMaintenanceEnds.map((entry, index) => (
                  <Table.Tr key={`${entry.customerName}|${entry.raw}|${index}`}>
                    <Table.Td>{entry.customerName}</Table.Td>
                    <Table.Td>{entry.raw}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Finding>
        </Stack>
      )}
    </Stack>
  );
}
